import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import { usePuoScrivere } from '../../utils/permessi';
import useAggiornamentoPeriodico from '../../hooks/useAggiornamentoPeriodico';
import FormAnagrafica from './FormAnagrafica';
import { TIPI_SOGGETTO, badge, scaricaFile } from './comuni';

const COLONNE = [
  { chiave: 'nome', titolo: 'Ragione sociale' },
  { chiave: 'codice_azienda', titolo: 'Codice azienda' },
  { chiave: 'codice_fiscale', titolo: 'Codice fiscale' },
  { chiave: 'partita_iva', titolo: 'P.IVA' },
  { chiave: 'tipo_soggetto', titolo: 'Tipo soggetto' },
  { chiave: 'stato', titolo: 'Stato' },
  { chiave: 'comune', titolo: 'Comune' },
];

// Elenco delle aziende dell'anagrafica comune: ricerca, filtri, contatori, ordinamento, paginazione, selezione per
// "Imposta tipo soggetto" in blocco (solo con la scrittura) ed esportazione Excel dell'elenco filtrato.
export default function Anagrafiche() {
  const puoScrivere = usePuoScrivere('anagrafiche');
  const navigate = useNavigate();
  const [cerca, setCerca] = useState('');
  const [cercaApplicata, setCercaApplicata] = useState('');
  const [filtri, setFiltri] = useState({ stato: '', tipo_soggetto: '', attiva: '' });
  const [ordine, setOrdine] = useState({ ordina: 'nome', verso: 'asc' });
  const [pagina, setPagina] = useState(1);
  const [dati, setDati] = useState({ aziende: [], totale: 0, per_pagina: 50, contatori: { totale: 0, da_completare: 0, senza_tipo: 0, archiviate: 0 } });
  const [selezionate, setSelezionate] = useState([]);
  const [tipoBlocco, setTipoBlocco] = useState('');
  const [esito, setEsito] = useState(null);
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [nuova, setNuova] = useState(false);

  // La ricerca parte poco dopo l'ultimo tasto
  useEffect(() => {
    const t = setTimeout(() => { setCercaApplicata(cerca.trim()); setPagina(1); }, 300);
    return () => clearTimeout(t);
  }, [cerca]);

  const parametri = useMemo(() => ({
    cerca: cercaApplicata || undefined,
    stato: filtri.stato || undefined,
    tipo_soggetto: filtri.tipo_soggetto || undefined,
    attiva: filtri.attiva || undefined,
    ordina: ordine.ordina, verso: ordine.verso,
  }), [cercaApplicata, filtri, ordine]);

  const carica = useCallback(async () => {
    try {
      const res = await api.get('/anagrafiche', { params: { ...parametri, pagina } });
      setDati(res.data);
      setSelezionate(sel => sel.filter(id => res.data.aziende.some(a => a.id === id)));
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Errore nel caricamento');
    }
  }, [parametri, pagina]);
  useEffect(() => { carica(); }, [carica]);
  useAggiornamentoPeriodico(carica, { attivo: !nuova && selezionate.length === 0 });

  const cambiaFiltro = (campo, valore) => { setFiltri(f => ({ ...f, [campo]: valore })); setPagina(1); };
  const ordinaPer = (chiave) => { setOrdine(o => ({ ordina: chiave, verso: o.ordina === chiave && o.verso === 'asc' ? 'desc' : 'asc' })); setPagina(1); };
  const pagine = Math.max(1, Math.ceil(dati.totale / dati.per_pagina));
  const tutte = dati.aziende.length > 0 && selezionate.length === dati.aziende.length;

  const impostaTipo = async () => {
    if (!tipoBlocco || !selezionate.length || inCorso) return;
    const n = selezionate.length;
    if (!window.confirm(`Impostare «${TIPI_SOGGETTO[tipoBlocco]}» come tipo di soggetto di ${n} ${n === 1 ? 'azienda' : 'aziende'}?`)) return;
    setErrore('');
    setInCorso(true);
    try {
      const righe = dati.aziende.filter(a => selezionate.includes(a.id)).map(a => ({ id: a.id, version: a.version }));
      const res = await api.post('/anagrafiche/imposta-tipo', { tipo_soggetto: tipoBlocco, righe });
      setEsito(res.data);
      setSelezionate([]);
      setTipoBlocco('');
      carica();
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Operazione non riuscita');
    } finally { setInCorso(false); }
  };

  const esporta = async () => {
    setErrore('');
    try { await scaricaFile(api, '/anagrafiche/export/excel', parametri, 'anagrafiche.xlsx'); } catch { setErrore('Esportazione non riuscita'); }
  };

  const c = dati.contatori;
  const frecce = chiave => (ordine.ordina === chiave ? (ordine.verso === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <Layout>
      <div style={stili.topBar}>
        <h2 style={{ margin: 0 }}>Anagrafiche</h2>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {puoScrivere && <button onClick={() => setNuova(true)} style={stili.btn}>+ Nuova azienda</button>}
          <button onClick={esporta} style={stili.btnSecondary}>Esporta Excel</button>
        </div>
      </div>

      <div style={st.contatori} aria-label="Contatori">
        <span><strong>{c.totale}</strong> aziende</span>
        <span><strong>{c.da_completare}</strong> da completare</span>
        <span><strong>{c.senza_tipo}</strong> senza tipo soggetto</span>
        {c.archiviate > 0 && <span><strong>{c.archiviate}</strong> archiviate</span>}
      </div>

      {errore && <div style={stili.errore}>{errore}</div>}

      <div style={st.filtri}>
        <input type="search" placeholder="Cerca per nome, alias, codice fiscale, P.IVA o codice azienda…" aria-label="Cerca" value={cerca} onChange={e => setCerca(e.target.value)} style={{ ...stili.input, flex: '3 1 260px' }} />
        <select aria-label="Stato" value={filtri.stato} onChange={e => cambiaFiltro('stato', e.target.value)} style={st.select}>
          <option value="">Tutte (stato)</option>
          <option value="da_completare">Da completare</option>
          <option value="completa">Complete</option>
        </select>
        <select aria-label="Tipo soggetto" value={filtri.tipo_soggetto} onChange={e => cambiaFiltro('tipo_soggetto', e.target.value)} style={st.select}>
          <option value="">Ogni tipo di soggetto</option>
          <option value="nessuno">Senza tipo soggetto</option>
          {Object.entries(TIPI_SOGGETTO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select aria-label="Attive o archiviate" value={filtri.attiva} onChange={e => cambiaFiltro('attiva', e.target.value)} style={st.select}>
          <option value="">Attive e archiviate</option>
          <option value="si">Solo attive</option>
          <option value="no">Solo archiviate</option>
        </select>
      </div>

      {puoScrivere && selezionate.length > 0 && (
        <div style={st.blocco}>
          <strong>{selezionate.length} selezionate</strong>
          <select aria-label="Tipo di soggetto da impostare" value={tipoBlocco} onChange={e => setTipoBlocco(e.target.value)} style={{ ...st.select, color: '#232323' }}>
            <option value="">Tipo di soggetto…</option>
            {Object.entries(TIPI_SOGGETTO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={impostaTipo} disabled={!tipoBlocco || inCorso} style={{ ...stili.btn, background: '#D3D678', color: '#232323', fontWeight: 600, opacity: !tipoBlocco ? 0.6 : 1 }}>
            {inCorso ? 'Operazione in corso…' : 'Imposta tipo soggetto'}
          </button>
          <button onClick={() => setSelezionate([])} style={stili.btnSmall}>Annulla selezione</button>
        </div>
      )}

      {esito && (
        <div role="status" style={{ ...st.esito, border: `1px solid ${esito.non_aggiornate.length ? '#fdba74' : '#86efac'}`, background: esito.non_aggiornate.length ? '#fff7ed' : '#ecfdf5' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
            <strong>
              {esito.aggiornate} {esito.aggiornate === 1 ? 'aggiornata' : 'aggiornate'}
              {esito.invariate ? `, ${esito.invariate} già con quel tipo` : ''}
              {esito.non_aggiornate.length ? `, ${esito.non_aggiornate.length} non aggiornate` : ''}
            </strong>
            <button onClick={() => setEsito(null)} style={stili.btnSmall}>Chiudi</button>
          </div>
          {esito.non_aggiornate.length > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem', color: '#9a3412' }}>
              {esito.non_aggiornate.map(x => <li key={x.id}><strong>{x.riferimento}</strong>: {x.motivo}</li>)}
            </ul>
          )}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={stili.table}>
          <thead>
            <tr>
              {puoScrivere && (
                <th style={{ ...stili.th, width: '2.2rem' }}>
                  <input type="checkbox" aria-label="Seleziona tutte" checked={tutte} onChange={e => setSelezionate(e.target.checked ? dati.aziende.map(a => a.id) : [])} />
                </th>
              )}
              {COLONNE.map(col => (
                <th key={col.chiave} style={stili.th} aria-sort={ordine.ordina === col.chiave ? (ordine.verso === 'asc' ? 'ascending' : 'descending') : undefined}>
                  <button type="button" onClick={() => ordinaPer(col.chiave)} style={st.intestazione}>{col.titolo}{frecce(col.chiave)}</button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dati.aziende.map(a => (
              <tr key={a.id} style={{ ...stili.tr, opacity: a.attiva ? 1 : 0.6, ...(selezionate.includes(a.id) ? { background: '#F7F7EA' } : {}) }}>
                {puoScrivere && (
                  <td style={stili.td}>
                    <input type="checkbox" aria-label={`Seleziona azienda ${a.id}`} checked={selezionate.includes(a.id)}
                      onChange={e => setSelezionate(s => (e.target.checked ? [...s, a.id] : s.filter(x => x !== a.id)))} />
                  </td>
                )}
                <td style={stili.td}>
                  <Link to={`/anagrafiche/${a.id}`} style={{ fontWeight: 600, color: '#232323' }}>{a.ragione_sociale}</Link>
                  {!a.attiva && <span style={{ ...badge.archiviata, marginLeft: '0.4rem' }}>Archiviata</span>}
                </td>
                <td style={st.codice}>{a.codice_azienda || <span style={st.nd}>—</span>}</td>
                <td style={st.codice}>{a.codice_fiscale || <span style={st.nd}>—</span>}</td>
                <td style={st.codice}>{a.partita_iva || <span style={st.nd}>—</span>}</td>
                <td style={stili.td}>{TIPI_SOGGETTO[a.tipo_soggetto] || <span style={st.nd}>—</span>}</td>
                <td style={stili.td}>{a.stato === 'da_completare' ? <span style={badge.daCompletare}>Da completare</span> : <span style={badge.completa}>Completa</span>}</td>
                <td style={stili.td}>{a.comune ? `${a.comune}${a.provincia ? ` (${a.provincia})` : ''}` : <span style={st.nd}>—</span>}</td>
              </tr>
            ))}
            {dati.aziende.length === 0 && (
              <tr><td colSpan={COLONNE.length + (puoScrivere ? 1 : 0)} style={{ ...stili.td, textAlign: 'center', color: '#999', padding: '1.5rem' }}>Nessuna azienda per questi filtri</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={st.piede}>
        <span>{dati.totale} {dati.totale === 1 ? 'azienda' : 'aziende'}</span>
        {pagine > 1 && (
          <nav aria-label="Pagine" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina <= 1} style={stili.btnSmall}>‹ Precedente</button>
            <span>Pagina {pagina} di {pagine}</span>
            <button onClick={() => setPagina(p => Math.min(pagine, p + 1))} disabled={pagina >= pagine} style={stili.btnSmall}>Successiva ›</button>
          </nav>
        )}
      </div>

      {nuova && <FormAnagrafica onChiudi={() => setNuova(false)} onSalvato={res => { setNuova(false); navigate(`/anagrafiche/${res.riga.id}`); }} />}
    </Layout>
  );
}

const st = {
  contatori: { display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '0.9rem', color: '#444', marginBottom: '0.8rem' },
  filtri: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.8rem' },
  select: { padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.88rem', background: 'white', flex: '1 1 150px', minWidth: 0 },
  blocco: { display: 'flex', alignItems: 'center', gap: '0.6rem', background: '#232323', color: 'white', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '0.8rem', flexWrap: 'wrap' },
  esito: { borderRadius: '8px', padding: '0.7rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.88rem' },
  intestazione: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 600, color: 'inherit', cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap' },
  codice: { padding: '0.5rem 0.6rem', fontSize: '0.86rem', verticalAlign: 'top', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  nd: { color: '#bbb' },
  piede: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem', margin: '0.8rem 0', fontSize: '0.88rem', color: '#555' },
};
